import React, { useState } from "react";
import {
  Box,
  Typography,
  // IconButton, // Temporarily commented - will be enabled later
  List,
  ListItem,
  ListItemButton,
  useTheme,
  alpha,
  useMediaQuery,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTranslation } from "react-i18next";
// import { Iconly } from 'react-iconly'; // Temporarily commented - will be enabled later
import { formatDate, formatDateTime } from "../../utils/formatUtils";
import RenameDialog from "./EditHistoryDialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import HistoryDrawerSkeleton from "./HistoryDrawerSkeleton";
import type { HistoryItem } from ".";
// import { Edit2 } from "iconsax-react"; // Temporarily commented - will be enabled later

export interface HistoryDrawerContentProps {
  items?: HistoryItem[];
  selectedItemId?: string;
  onItemClick?: (item: HistoryItem) => void;
  onEdit?: (item: HistoryItem) => void;
  onDelete?: (item: HistoryItem) => void;
  showTitle?: boolean;
  isLoading?: boolean;
  showCompanyHistory?: boolean;
  onCompanyHistoryToggle?: (enabled: boolean) => void;
  showNameAsTitle?: boolean;
}

const HistoryDrawerContent: React.FC<HistoryDrawerContentProps> = ({
  items,
  selectedItemId,
  onItemClick,
  onEdit,
  onDelete,
  showTitle = false,
  isLoading = false,
  showCompanyHistory = false,
  onCompanyHistoryToggle,
  showNameAsTitle = false,
}) => {
  const { t } = useTranslation();
  // Use provided items or empty array if none
  // Use provided items or empty array if none, sorted with isMyDocument items first
  const itemsToUse = React.useMemo(() => {
    const list = items || [];
    return [...list].sort((a, b) => {
      // Items with isMyDocument: true should come first
      if (a.isMyDocument && !b.isMyDocument) return -1;
      if (!a.isMyDocument && b.isMyDocument) return 1;
      return 0;
    });
  }, [items]);
  const theme = useTheme();
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState<HistoryItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<HistoryItem | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // Temporarily commented - will be enabled later
  // const handleEditClick = (e: React.MouseEvent, item: HistoryItem) => {
  //   e.stopPropagation();
  //   setItemToRename(item);
  //   setRenameDialogOpen(true);
  // };

  // const handleDeleteClick = (e: React.MouseEvent, item: HistoryItem) => {
  //   e.stopPropagation();
  //   setItemToDelete(item);
  //   setDeleteDialogOpen(true);
  // };

  const handleDeleteConfirm = () => {
    if (itemToDelete) {
      if (onDelete) {
        onDelete(itemToDelete);
      } else {
        console.log("Delete item:", itemToDelete);
      }
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleRenameConfirm = (newName: string) => {
    if (itemToRename) {
      if (onEdit) {
        onEdit({ ...itemToRename, name: newName });
      } else {
        console.log("Rename item:", { ...itemToRename, name: newName });
      }
    }
    setRenameDialogOpen(false);
    setItemToRename(null);
  };

  const formatTimestamp = (timestamp: string | Date): string => {
    if (!timestamp) return "";
    try {
      return formatDateTime(timestamp, true);
    } catch {
      return formatDate(timestamp);
    }
  };

  const capitalizeFirstLetter = (text: string | undefined): string => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const formatWorkflowType = (workflowType: string): string => {
    const workflowMap: Record<string, string> = {
      wordtopdf: t("components.historyDrawer.workflowTypes.wordToPdf"),
      pdftoword: t("components.historyDrawer.workflowTypes.pdfToWord"),
      xlsxtopdf: t("components.historyDrawer.workflowTypes.excelToPdf"),
      pdftoxlsx: t("components.historyDrawer.workflowTypes.pdfToExcel"),
      comparespecs: t("components.historyDrawer.workflowTypes.compareSpecs"),
      extracttext: t("components.historyDrawer.workflowTypes.extractText"),
      documentcontradiction: t("components.historyDrawer.workflowTypes.documentContradiction"),
      question: t("components.historyDrawer.workflowTypes.question"),
      technischeschatting: t("components.historyDrawer.workflowTypes.technicalEstimate"),
      technicalestimate: t("components.historyDrawer.workflowTypes.technicalEstimate"),
      documentsamenvatting: t("components.historyDrawer.workflowTypes.documentSummary"),
      gegevensextractie: t("components.historyDrawer.workflowTypes.dataExtraction"),
      companystandard: t("components.historyDrawer.workflowTypes.companyStandard"),
      requestspecs: t("components.historyDrawer.workflowTypes.requestSpecs"),
    };
    return workflowMap[workflowType?.toLowerCase()] || workflowType;
  };
  const formatWorkflow = (workflow: string): string => {
    const workflowMap: Record<string, string> = {
      documentcontradiction: t("components.historyDrawer.workflowTypes.documentContradiction"),
      conversion: t("components.historyDrawer.workflowNames.conversion"),
      comparison: t("components.historyDrawer.workflowNames.comparison"),
      dataanalysis: t("components.historyDrawer.workflowNames.dataAnalysis"),
      summarize: t("components.historyDrawer.workflowNames.summarize"),
      question: t("components.historyDrawer.workflowTypes.question"),
      technischeschatting: t("components.historyDrawer.workflowTypes.technicalEstimate"),
      technicalestimate: t("components.historyDrawer.workflowTypes.technicalEstimate"),
      aicomparison: t("components.historyDrawer.workflowNames.aiComparison"),
      companystandard: t("components.historyDrawer.workflowTypes.companyStandard"),
    };
    return workflowMap[workflow?.toLowerCase()] || workflow;
  };

  return (
    <>
      <Box
        sx={{
          p: isMobile ? 1 : 3,
          pt: showTitle ? 3 : 0,
          display: "flex",
          flex: 1,
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {showTitle && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {t("components.historyDrawer.title")}
              </Typography>
            </Box>

            {onCompanyHistoryToggle && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showCompanyHistory}
                    onChange={(e) => onCompanyHistoryToggle(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {t("components.historyDrawer.companyHistoryToggle")}
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            )}
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pr: 0,
            minHeight: 0,
          }}
        >
          {isLoading ? (
            <HistoryDrawerSkeleton />
          ) : itemsToUse.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: 200,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("components.historyDrawer.noHistoryAvailable")}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {itemsToUse.map((item) => {
                const isSelected = selectedItemId === item.id;
                const isMyDocumentHighlight = item.isMyDocument;

                return (
                  <ListItem
                    key={item.id}
                    disablePadding
                    sx={{
                      mb: 1.5,
                      "&:last-child": {
                        mb: 0,
                      },
                    }}
                  >
                    <ListItemButton
                      onClick={() => onItemClick?.(item)}
                      selected={isSelected}
                      sx={{
                        borderRadius: 1.5,
                        py: 1.5,
                        px: 2,
                        backgroundColor: isSelected
                          ? alpha(theme.palette.primary.main, 0.16)
                          : "transparent",
                        borderLeft: isMyDocumentHighlight
                          ? `4px solid ${theme.palette.primary.main}`
                          : "4px solid transparent",
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.12,
                          ),
                        },
                        "&.Mui-selected": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.16,
                          ),
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.12,
                            ),
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: isSelected ? 600 : 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {showNameAsTitle
                            ? item.name
                            : `${capitalizeFirstLetter(formatWorkflow(item.workflow || ""))} - ${formatWorkflowType(item.workflowType || "")}`}
                        </Typography>
                        {item.userName && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.grey[600],
                            }}
                          >
                            <strong style={{ color: theme.palette.grey[700] }}>
                              {t("components.historyDrawer.byLabel")}
                            </strong>{" "}
                            {item.userName}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.grey[600],
                          }}
                        >
                          <strong style={{ color: theme.palette.grey[700] }}>
                            {t("components.historyDrawer.fileLabel")}
                          </strong>{" "}
                          {item.name}
                        </Typography>
                        {item.workflowType && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.grey[600],
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <strong style={{ color: theme.palette.grey[700] }}>
                              {t("components.historyDrawer.dateLabel")}
                            </strong>{" "}
                            {formatTimestamp(item.timestamp)}
                          </Typography>
                        )}
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Box>

      {/* Rename Dialog */}
      <RenameDialog
        open={renameDialogOpen}
        onClose={() => {
          setRenameDialogOpen(false);
          setItemToRename(null);
        }}
        onConfirm={handleRenameConfirm}
        currentName={itemToRename?.name || ""}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={itemToDelete?.name}
      />
    </>
  );
};

export default HistoryDrawerContent;
